import React from 'react';
import MyTypes from 'MyTypes';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Modal, Button, Spinner, Form, Badge } from 'react-bootstrap';
import { request, ApiResponse } from 'services/api/base';
import { FaRobot, FaFileArchive, FaListUl, FaCheckCircle, FaRegCircle } from 'react-icons/fa';
import { aiLabActions } from 'store/aiLab';
import { userActions } from 'store/user';
import { AppToastTypes } from 'models/enums';
import * as models from 'models';
import RuntimeBundleDropzone from './execution/RuntimeBundleDropzone';
import RuntimeBundleReview, { StagedFileChip, StagedFileMeta, slotFiles } from './execution/RuntimeBundleReview';
import { AI_DOCUMENT_ACCEPT } from './aiDocumentFileTypes';

interface Props extends PropsFromRedux {
    toolId: number;
    toolName?: string;
    claimId: number;
    onClose: () => void;
    onExecutionSuccess?: () => void;
}

type InputMode = 'bundle' | 'manual';

const footerButtonStyle: React.CSSProperties = {
    width: 'auto',
    minWidth: '100px',
    minHeight: '38px',
    padding: '0.5rem 1.25rem',
    whiteSpace: 'nowrap'
};

interface State {
    inputValues: { [slotId: string]: string | string[] };
    isUploading: boolean;
    inputMode: InputMode | null;
    fileMeta: { [blob: string]: StagedFileMeta };
    bundlePool: string[];
    runInBackground: boolean;
    submittedInBackground: boolean;
}

export class AiExecutionModal extends React.Component<Props, State> {
    public state: State = {
        inputValues: {},
        isUploading: false,
        inputMode: null,
        fileMeta: {},
        bundlePool: [],
        runInBackground: true,
        submittedInBackground: false
    };

    private pollTimer: NodeJS.Timeout | null = null;

    public componentDidMount() {
        this.props.requestExecuteAITool(this.props.claimId, this.props.toolId);
    }
    
    public componentDidUpdate(prevProps: Props) {     
        // When tool moves to Queued state (from either direct launch or resume), initiate polling
        if (prevProps.executionStatus !== 'Queued' && this.props.executionStatus === 'Queued' && this.props.executionLogId) {
            if (this.state.submittedInBackground) {
                this.continueInBackground();
                return;
            }
            this.pollExecutionStatus(this.props.executionLogId);
        }

        // Notify parent caller if execution transitions to complete
        if (prevProps.executionStatus !== 'Complete' && this.props.executionStatus === 'Complete') {
            if (this.props.onExecutionSuccess) {
                this.props.onExecutionSuccess();
            }
        }

        if (prevProps.executionStatus !== 'PendingInput' && this.props.executionStatus === 'PendingInput' && this.state.inputMode === null) {
            this.setState({ inputMode: 'bundle' });
        }

        const matchResult = this.props.bundleMatch.result;
        if (matchResult && matchResult !== prevProps.bundleMatch.result) {
            this.applyBundleMatch(matchResult);
        }
    }

    public componentWillUnmount() {
        this.clearPolling();
        this.props.resetAIToolExecution();
    }

    private clearPolling = () => {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    };

    private continueInBackground = () => {
        this.clearPolling();
        const claimSlot = this.props.requiredFiles.find(f => f.type === 'claim');
        const enteredClaimNumber = claimSlot ? (this.state.inputValues[claimSlot.slotId] as string || '').trim() : '';
        const destination = this.props.claimId > 0
            ? "the claim's activity log"
            : enteredClaimNumber
                ? `claim ${enteredClaimNumber}'s activity log`
                : "the tool's run history";
        this.props.setAppToast({
            message: `AI tool queued. Results will post to ${destination} when complete.`,
            type: AppToastTypes.Success
        });
        this.props.onClose();
    };

    private applyBundleMatch = (result: models.AiRuntimeBundleMatch) => {
        this.setState(prev => {
            const fileMeta = { ...prev.fileMeta };
            const inputValues: State['inputValues'] = {};

            Object.keys(prev.inputValues).forEach(slotId => {
                const value = prev.inputValues[slotId];
                inputValues[slotId] = Array.isArray(value)
                    ? value.filter(blob => fileMeta[blob]?.source !== 'bundle')
                    : value;
            });

            result.slots.forEach(slot => {
                const blobs = slot.files.map(f => {
                    fileMeta[f.blobFileName] = {
                        name: f.originalFileName,
                        source: 'bundle',
                        confidence: f.confidence,
                        reason: f.reason
                    };
                    return f.blobFileName;
                });
                if (blobs.length > 0) {
                    inputValues[slot.slotId] = [...slotFiles(inputValues, slot.slotId), ...blobs];
                }
            });

            result.unmatchedFiles.forEach(f => {
                fileMeta[f.blobFileName] = { name: f.originalFileName, source: 'bundle' };
            });

            return {
                inputValues,
                fileMeta,
                bundlePool: result.unmatchedFiles.map(f => f.blobFileName)
            };
        });
    };

    private assignFile = (blob: string, slotId: string | null) => {
        this.setState(prev => {
            const inputValues: State['inputValues'] = {};
            Object.keys(prev.inputValues).forEach(key => {
                const value = prev.inputValues[key];
                inputValues[key] = Array.isArray(value) ? value.filter(b => b !== blob) : value;
            });

            let bundlePool = prev.bundlePool.filter(b => b !== blob);
            const meta = prev.fileMeta[blob];
            const fileMeta = { ...prev.fileMeta };

            if (slotId) {
                inputValues[slotId] = [...slotFiles(inputValues, slotId), blob];
                if (meta) {
                    fileMeta[blob] = { ...meta, confidence: undefined, reason: undefined };
                }
            } else if (meta?.source === 'bundle') {
                bundlePool = [...bundlePool, blob];
            }

            return { inputValues, bundlePool, fileMeta };
        });
    };

    private handleReplaceBundle = () => {
        this.setState(prev => {
            const inputValues: State['inputValues'] = {};
            Object.keys(prev.inputValues).forEach(slotId => {
                const value = prev.inputValues[slotId];
                inputValues[slotId] = Array.isArray(value)
                    ? value.filter(blob => prev.fileMeta[blob]?.source !== 'bundle')
                    : value;
            });
            return { inputValues, bundlePool: [] };
        });
        this.props.clearRuntimeBundleMatch();
    };

    private uploadFilesToSlot = async (files: FileList, slotId: string) => {
        if (!files || files.length === 0) return;

        this.setState({ isUploading: true });
        const uploaded: Array<{ blob: string; name: string }> = [];

        try {
            for (let i = 0; i < files.length; i++) {
                const formData = new FormData();
                formData.append('file', files[i]);

                const response: ApiResponse = await request({
                    method: 'POST',
                    url: '/ailab/upload-sandbox-file',
                    data: formData,
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                if (response.isSuccess) {
                    uploaded.push({ blob: response.data.blobFileName, name: files[i].name });
                } else {
                    alert(`Failed to upload ${files[i].name}: ${response.getError()}`);
                }
            }

            if (uploaded.length > 0) {
                this.setState(prev => {
                    const fileMeta = { ...prev.fileMeta };
                    uploaded.forEach(u => { fileMeta[u.blob] = { name: u.name, source: 'manual' }; });
                    return {
                        fileMeta,
                        inputValues: {
                            ...prev.inputValues,
                            [slotId]: [...slotFiles(prev.inputValues, slotId), ...uploaded.map(u => u.blob)]
                        }
                    };
                });
            }
        } catch (err: any) {
            alert(`An error occurred during file upload: ${err.message}`);
        } finally {
            this.setState({ isUploading: false });
        }
    };

    private handleTextChange = (value: string, slotId: string) => {
        this.setState(prev => ({
            inputValues: {
                ...prev.inputValues,
                [slotId]: value
            }
        }));
    };

    private handleSubmitRequirements = () => {
        const { claimId, toolId, submitAIToolInput, requiredFiles } = this.props;
        const { inputValues, fileMeta } = this.state;

        const runtimeAttachments: any[] = [];

        requiredFiles.forEach(field => {
            const isFile = field.type === 'file';
            const value = inputValues[field.slotId];

            if (isFile && (!value || (Array.isArray(value) && value.length === 0))) {
                return;
            }

            if (isFile && Array.isArray(value)) {
                value.forEach((blobName, index) => {
                    runtimeAttachments.push({
                        slotIdentifier: field.slotId,
                        blobFileName: blobName,
                        textValue: '',
                        displayName: fileMeta[blobName]?.name || `Uploaded Context Document (${index + 1})`
                    });
                });
            } else {
                runtimeAttachments.push({
                    slotIdentifier: field.slotId,
                    blobFileName: '',
                    textValue: typeof value === 'string' ? value : '',
                    displayName: field.type === 'claim' ? 'Claim Number Input' : 'User Text Input'
                });
            }
        });

        this.setState({ submittedInBackground: this.state.runInBackground });

        if (submitAIToolInput) {
            submitAIToolInput({
                claimId,
                toolId,
                inputPayload: {
                    runtimeAttachments
                }
            });
        }
    };

    private pollExecutionStatus = (executionLogId: number) => {
        this.clearPolling();

        this.pollTimer = setInterval(async () => {
            try {
                const res: ApiResponse = await request({
                    method: 'GET',
                    url: `/ailab/executions/${executionLogId}/status`
                });

                if (res.isSuccess && res.data) {
                    const data = res.data;
                    if (data.status === 'Complete') {
                        this.clearPolling();
                        this.props.setExecutionComplete(data.outputHtml, this.props.claimId);
                    } else if (data.status === 'Failed') {
                        this.clearPolling();
                        this.props.setExecutionFailed(data.errorMessage || 'Execution failed on background processor.');
                    }
                }
            } catch (err: any) {
                this.clearPolling();
                this.props.setExecutionFailed(err.message || 'Error communicating with execution polling endpoint.');
            }
        }, 3000);
    };

    private renderRequirementBadge(inputSlot: models.AiRequiredInput) {
        return inputSlot.isRequired === false ? (
            <Badge bg="secondary" className="text-uppercase text-white px-2 py-1" style={{ fontSize: '9px' }}>Optional Component</Badge>
        ) : (
            <Badge bg="danger" className="text-uppercase text-white px-2 py-1" style={{ fontSize: '9px' }}>★ Required Context</Badge>
        );
    }

    private renderTextInput(inputSlot: models.AiRequiredInput) {
        const { inputValues } = this.state;
        return (
            <Form.Group key={inputSlot.slotId} className="mb-3 bg-white p-3 rounded border shadow-sm">
                <Form.Label className="small fw-bold text-uppercase text-muted d-flex justify-content-between align-items-center mb-2">
                    <span>{inputSlot.label}</span>
                    {this.renderRequirementBadge(inputSlot)}
                </Form.Label>
                <Form.Control 
                    type="text" 
                    size="sm" 
                    placeholder={
                        inputSlot.type === 'claim'
                            ? 'Enter claim / file number...'
                            : inputSlot.isRequired === false
                                ? 'Enter optional criteria (or leave blank)...'
                                : 'Enter required criteria...'
                    }
                    value={(inputValues[inputSlot.slotId] as string) || ''}
                    onChange={(e: any) => this.handleTextChange(e.target.value, inputSlot.slotId)}
                />
            </Form.Group>
        );
    }

    private renderManualFileInput(inputSlot: models.AiRequiredInput) {
        const { inputValues, fileMeta } = this.state;
        const files = slotFiles(inputValues, inputSlot.slotId);
        return (
            <Form.Group key={inputSlot.slotId} className="mb-3 bg-white p-3 rounded border shadow-sm">
                <Form.Label className="small fw-bold text-uppercase text-muted d-flex justify-content-between align-items-center mb-2">
                    <span>
                        {inputSlot.stepTitle || inputSlot.label}
                        {inputSlot.stepTitle && inputSlot.label !== inputSlot.stepTitle && (
                            <span className="fw-normal text-lowercase ms-2">({inputSlot.label})</span>
                        )}
                    </span>
                    {this.renderRequirementBadge(inputSlot)}
                </Form.Label>
                <Form.Control 
                    type="file" 
                    size="sm" 
                    multiple 
                    accept={AI_DOCUMENT_ACCEPT}
                    onChange={(e: any) => {
                        this.uploadFilesToSlot(e.target.files, inputSlot.slotId);
                        e.target.value = '';
                    }}
                />
                {files.length > 0 && (
                    <div className="mt-2">
                        {files.map(blob => (
                            <StagedFileChip key={blob} blob={blob} meta={fileMeta[blob]} onRemove={() => this.assignFile(blob, null)} />
                        ))}
                    </div>
                )}
            </Form.Group>
        );
    }

    private renderModeOption(mode: InputMode, icon: React.ReactNode, title: string, description: string) {
        const isSelected = this.state.inputMode === mode;
        return (
            <div className="col-md-6">
                <div
                    role="radio"
                    aria-checked={isSelected}
                    tabIndex={0}
                    onClick={() => this.setState({ inputMode: mode })}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && this.setState({ inputMode: mode })}
                    className="d-flex align-items-start h-100 p-3 rounded shadow-sm"
                    style={{
                        cursor: 'pointer',
                        border: `2px solid ${isSelected ? '#198754' : '#dee2e6'}`,
                        backgroundColor: isSelected ? '#f1faf5' : '#ffffff',
                        transition: 'border-color 0.15s ease-in-out'
                    }}
                >
                    <div className={`me-3 mt-1 ${isSelected ? 'text-success' : 'text-secondary'}`}>{icon}</div>
                    <div className="flex-grow-1">
                        <div className={`fw-bold ${isSelected ? 'text-success' : 'text-dark'}`}>{title}</div>
                        <div className="small text-muted">{description}</div>
                    </div>
                    {isSelected
                        ? <FaCheckCircle size={18} className="text-success ms-2 flex-shrink-0" />
                        : <FaRegCircle size={18} className="text-secondary ms-2 flex-shrink-0" />}
                </div>
            </div>
        );
    }

    private renderModeToggle() {
        return (
            <div className="row g-3 mb-3" role="radiogroup" aria-label="Document input method">
                {this.renderModeOption(
                    'bundle',
                    <FaFileArchive size={24} />,
                    'Auto-Match Documents',
                    'Drop a zip file or individual files and AI assigns each document to its input.'
                )}
                {this.renderModeOption(
                    'manual',
                    <FaListUl size={24} />,
                    'Manual Upload',
                    'Upload documents one slot at a time.'
                )}
            </div>
        );
    }

    private renderBundleView(fileSlots: models.AiRequiredInput[]) {
        const { bundleMatch, toolId } = this.props;
        const { inputValues, fileMeta, bundlePool } = this.state;

        if (bundleMatch.result) {
            return (
                <RuntimeBundleReview
                    match={bundleMatch.result}
                    fileSlots={fileSlots}
                    assignments={inputValues}
                    fileMeta={fileMeta}
                    pool={bundlePool}
                    onAssign={this.assignFile}
                    onUploadToSlot={this.uploadFilesToSlot}
                    onReplaceBundle={this.handleReplaceBundle}
                />
            );
        }

        return (
            <RuntimeBundleDropzone
                isMatching={bundleMatch.isMatching}
                error={bundleMatch.error}
                onFilesSelected={(files) => this.props.requestMatchRuntimeBundle(toolId, files)}
            />
        );
    }

    public render() {
        const { isExecuting, executionStatus, executionLogId, outputHtml, requiredFiles, errorMessage, bundleMatch } = this.props;
        const { inputValues, isUploading, inputMode, runInBackground, submittedInBackground } = this.state;

        const orderedRequiredInputs = [...requiredFiles].sort((a, b) => {
            if (a.type === 'claim' && b.type !== 'claim') return -1;
            if (b.type === 'claim' && a.type !== 'claim') return 1;
            return 0;
        });
        const nonFileInputs = orderedRequiredInputs.filter(f => f.type !== 'file');
        const fileSlots = orderedRequiredInputs.filter(f => f.type === 'file');

        const allRequiredInputsProvided = orderedRequiredInputs.every(f => {
            if (f.isRequired === false) return true; 
            
            const val = inputValues[f.slotId];
            if (f.type === 'file') {
                return Array.isArray(val) && val.length > 0;
            }
            return typeof val === 'string' && !!val.trim();
        });

        const isPending = !isExecuting && !isUploading && executionStatus === 'PendingInput';
        const isBusy = isExecuting || isUploading || bundleMatch.isMatching;
        const isWaitingOnQueue = executionStatus === 'Queued' && !!executionLogId && !submittedInBackground;
        const modalSize = executionStatus === 'PendingInput' && fileSlots.length > 0
            ? 'xl'
            : executionStatus === 'Complete'
                ? 'lg'
                : undefined;

        return (
            <Modal centered={true} show={true} onHide={this.props.onClose} size={modalSize} backdrop="static">
                <Modal.Header closeButton={!isBusy}>
                    <Modal.Title className="d-flex align-items-center flex-wrap">
                        <FaRobot className="me-2 text-success" /> AI Tool Execution Coordinator
                        {this.props.toolName && (
                            <span className="ms-2 text-muted fw-normal">— {this.props.toolName}</span>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '200px' }}>
                    
                    {(isExecuting || isUploading) && (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5">
                            <Spinner animation="border" variant="success" className="mb-3" />
                            <h6 className="fw-bold text-dark">
                                {isUploading 
                                    ? 'Uploading Claim Bundle Documents...' 
                                    : executionStatus === 'Initiating' 
                                        ? 'Evaluating Pipeline Context...' 
                                        : executionStatus === 'Queued'
                                            ? 'Queued / Processing in Background...'
                                            : 'AI Engine Executing...'}
                            </h6>
                            <p className="small text-muted text-center mb-0">Processing active data assets. Please hold tightly.</p>
                        </div>
                    )}

                    {!isExecuting && !isUploading && executionStatus === 'Failed' && (
                        <div className="alert alert-danger border-0 shadow-sm">
                            <h6 className="fw-bold">Execution Error Intercepted</h6>
                            <p className="small mb-0">{errorMessage}</p>
                        </div>
                    )}

                    {isPending && (
                        <div>
                            <div className="bg-white p-3 rounded shadow-sm border border-warning mb-4">
                                <h6 className="fw-bold text-warning mb-1">Additional Context Required</h6>
                                <p className="small text-muted mb-0">
                                    {fileSlots.length > 0
                                        ? 'This tool needs documents. Drop a zip file or individual files to auto-match them, or upload each document manually.'
                                        : 'This tool configuration needs extra confirmation metrics. Provide details below to complete pipeline steps.'}
                                </p>
                            </div>

                            {nonFileInputs.map(inputSlot => this.renderTextInput(inputSlot))}

                            {fileSlots.length > 0 && (
                                <>
                                    {this.renderModeToggle()}
                                    {inputMode === 'bundle'
                                        ? this.renderBundleView(fileSlots)
                                        : fileSlots.map(inputSlot => this.renderManualFileInput(inputSlot))}
                                </>
                            )}
                        </div>
                    )}

                    {!isExecuting && !isUploading && executionStatus === 'Complete' && (
                        <div className="bg-white p-4 rounded border shadow-sm markdown-body">
                            <div dangerouslySetInnerHTML={{ __html: outputHtml }} />
                        </div>
                    )}

                </Modal.Body>
                <Modal.Footer className="d-flex flex-wrap align-items-center" style={{ rowGap: '0.5rem' }}>
                    <div className="flex-grow-1 me-3" style={{ minWidth: '220px' }}>
                        {isPending && (
                            <>
                                <Form.Check
                                    type="switch"
                                    id="ai-run-in-background"
                                    className="fw-bold small mb-0"
                                    label="Run in background"
                                    checked={runInBackground}
                                    onChange={(e: any) => this.setState({ runInBackground: e.target.checked })}
                                />
                                <div className="small text-muted">
                                    {runInBackground
                                        ? 'Closes this window once queued so you can move on.'
                                        : 'Keeps this window open and shows the result when it finishes.'}
                                </div>
                            </>
                        )}
                        {isWaitingOnQueue && (
                            <span className="small text-muted">You can close this window; the run will keep going.</span>
                        )}
                    </div>
                    <div className="d-flex flex-wrap justify-content-end ms-auto" style={{ gap: '0.5rem' }}>
                        <Button variant="secondary" onClick={this.props.onClose} disabled={isBusy} style={footerButtonStyle}>
                            {executionStatus === 'Complete' ? 'DISMISS' : 'CANCEL'}
                        </Button>
                        {isWaitingOnQueue && (
                            <Button
                                variant="success"
                                className="fw-bold"
                                onClick={this.continueInBackground}
                                style={{ ...footerButtonStyle, backgroundColor: '#ffffff', color: '#198754', border: '1px solid #198754' }}
                            >
                                CONTINUE IN BACKGROUND
                            </Button>
                        )}
                        {isPending && (
                            <Button 
                                variant="success" 
                                className={`fw-bold text-white ${!allRequiredInputsProvided || bundleMatch.isMatching ? 'disabled' : ''}`}
                                disabled={!allRequiredInputsProvided || bundleMatch.isMatching} 
                                onClick={this.handleSubmitRequirements}
                                style={{ ...footerButtonStyle, backgroundColor: '#198754' }}
                            >
                                {runInBackground ? 'RUN IN BACKGROUND' : 'RUN & WAIT'}
                            </Button>
                        )}
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}

const mapStateToProps = (state: MyTypes.RootState) => ({
    isExecuting: state.aiLab.aiExecution.isExecuting,
    executionStatus: state.aiLab.aiExecution.executionStatus,
    executionLogId: state.aiLab.aiExecution.executionLogId,
    errorMessage: state.aiLab.aiExecution.errorMessage,
    outputHtml: state.aiLab.aiExecution.outputHtml,
    requiredFiles: state.aiLab.aiExecution.requiredFiles,
    bundleMatch: state.aiLab.aiExecution.bundleMatch
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {
            requestExecuteAITool: aiLabActions.requestExecuteAITool,
            submitAIToolInput: aiLabActions.submitAIToolInput,
            resetAIToolExecution: aiLabActions.resetAIToolExecution,
            setExecutionComplete: aiLabActions.setExecutionComplete,
            setExecutionFailed: aiLabActions.setExecutionFailed,
            requestMatchRuntimeBundle: aiLabActions.requestMatchRuntimeBundle,
            clearRuntimeBundleMatch: aiLabActions.clearRuntimeBundleMatch,
            setAppToast: userActions.setAppToast
        },
        dispatch
    );

const connector = connect(mapStateToProps, mapDispatchToProps); 
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(AiExecutionModal);
